import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { RetryApiAlert } from '../../shared/components/RetryApiAlert/RetryApiAlert';
import { PipelineAPI } from '../api/PipelineAPI';
import { IntegrationFactory } from '../factories/integrationFactory';
import { GenericPageShimmer } from '../../shimmer/GenericPageShimmer/GenericPageShimmer';
import { EditPipelineContextProvider } from './components/EditPipelineContext';
import { getErrorMessageFromAxiosError } from '../../request/utils';
import { EditPipelineWizard } from './EditPipelineWizard';
import { useQuery } from '../../request/hooks/useQuery';
import { EntityUIState } from '../../request/interfaces/entity-ui-state';
import { Integration } from '../../shared/interfaces/integration';
import { useEditPipelineContext } from './hooks/useEditPipelineContext';
import { EDIT_PIPELINE_DATA_IDS } from './dataIds';

export default function EditPipeline() {
  const { id: pipelineId } = useParams();

  const {
    data: pipeline,
    fetch: editAndFetchIntegration,
    uiState,
    error
  } = useQuery({
    dataSource$: () =>
      PipelineAPI.editPipeline(Number(pipelineId)).then(res => {
        return IntegrationFactory(res.data);
      })
  });

  useEffect(() => {
    editAndFetchIntegration();
  }, [pipelineId]);

  const dataIds = EDIT_PIPELINE_DATA_IDS;

  return (
    <EditPipelineContextProvider>
      {uiState === EntityUIState.LOADING ? <GenericPageShimmer dataId={dataIds.shimmer} /> : null}

      {uiState === EntityUIState.IDLE && pipeline ? (
        <EditPipelineInner initialPipeline={pipeline} />
      ) : null}

      {uiState === EntityUIState.ERRORED && error ? (
        <RetryApiAlert
          title='Failed to fetch the Pipeline'
          subTitle={getErrorMessageFromAxiosError(error)}
          secondaryCTAProps={{
            children: 'Retry',
            onClick: editAndFetchIntegration
          }}
          className='h-100'
          dataId={dataIds.retry}
        />
      ) : null}
    </EditPipelineContextProvider>
  );
}

function EditPipelineInner({ initialPipeline }: { initialPipeline: Integration }) {
  const { updatePipeline, pipeline } = useEditPipelineContext();

  useEffect(() => {
    updatePipeline(initialPipeline);
  }, [initialPipeline]);

  return pipeline ? <EditPipelineWizard /> : null;
}
